import React, { useContext } from 'react'
import { useParams } from 'react-router-dom'

import Link from 'components/Link'
import Navbar from 'components/Navbar'
import VersionLabel from 'components/VersionLabel'
import { useForTablet } from 'components/Breakpoint'
import Zoom from 'components/Zoom'
import Tooltip from 'components/Tooltip'
import { routes, RouteParams } from 'constants/routes'

import { DocumentSidebarLayoutHeaderProps } from 'containers/DocumentSidebarLayout'
import { ModalContext } from 'containers/ModalManager'
import { LaunchSketchAlert } from 'containers/modals/LaunchSketchAlert'

import HeaderWorkspaceBadge from 'modules/shares/components/HeaderWorkspaceBadge'
import HeaderNavigation from 'modules/shares/components//HeaderNavigation'
import { usePageCanvasZoom } from 'modules/shares/components/WebRenderer/utils/WebRendererCameraContext'
import {
  MIN_ZOOM,
  MAX_ZOOM,
} from 'modules/shares/components/WebRenderer/constants'

import UserNavbarSection from 'modules/user/components/UserNavbarSection'
import { useVersioning } from 'modules/versioning'

import { ReactComponent as ExternalIcon } from 'icons/square.arrow.up.right-24.svg'

type PageCanvasViewHeaderProps = Pick<
  DocumentSidebarLayoutHeaderProps,
  'setSidebarLeftOpen'
>

function PageCanvasViewHeader({
  setSidebarLeftOpen,
}: PageCanvasViewHeaderProps) {
  const { showModal } = useContext(ModalContext)
  const { share, isViewingLatestVersion } = useVersioning()
  const isTabletAndBigger = useForTablet()
  const { zoom, setZoom, setZoomToFit } = usePageCanvasZoom()
  const userCanOpenInApp = useGetCanOpenInApp()
  const { pageUUID } = useParams<RouteParams<'SHARE_PAGE_CANVAS_VIEW'>>()

  return (
    <Navbar>
      <HeaderWorkspaceBadge
        setSidebarLeftOpen={setSidebarLeftOpen}
        workspace={share.workspace!}
      />
      <HeaderNavigation currentView="pageCanvas" />
      <Navbar.Section align="end">
        {!isViewingLatestVersion && isTabletAndBigger && (
          <>
            <VersionLabel
              as={Link}
              to={routes.SHARE_PAGE_CANVAS_VIEW.create({
                shareID: share.identifier,
                pageUUID: pageUUID,
              })}>
              see latest update
            </VersionLabel>
            <Navbar.Divider />
          </>
        )}
        <Zoom
          zoom={{ type: 'ZOOM_FACTOR', factor: zoom }}
          onZoomChanged={zoomObject => {
            if (zoomObject.type === 'ZOOM_TO_FIT') {
              setZoomToFit()
            } else if (typeof zoomObject.factor !== 'undefined') {
              setZoom(zoomObject.factor)
            }
          }}
          minZoom={MIN_ZOOM}
          maxZoom={MAX_ZOOM}
        />
        {userCanOpenInApp && isTabletAndBigger && (
          <Tooltip
            content={
              isViewingLatestVersion
                ? 'Edit'
                : 'Select the latest update of this document to open it in the Mac app'
            }
            placement="bottom">
            <Navbar.Button
              size={24}
              icon={ExternalIcon}
              onClick={() =>
                showModal(LaunchSketchAlert, {
                  shareId: share.identifier,
                  action: 'openInSketch',
                })
              }
              disabled={!isViewingLatestVersion}
            />
          </Tooltip>
        )}
        {isTabletAndBigger && (
          <>
            <Navbar.Divider />
            <UserNavbarSection />
          </>
        )}
      </Navbar.Section>
    </Navbar>
  )
}

function useGetCanOpenInApp() {
  const versionContext = useVersioning()
  const { currentVersion } = (!versionContext.loading && versionContext) || {}

  return currentVersion?.document?.userCanOpenInApp ?? false
}

export default PageCanvasViewHeader
